// src/components/ml/IntelligenceOverview.js
// AI Intelligence Center → Overview: every model, every AI engine, where each one is used,
// the data behind them, what needs attention, and the latest activity — from one API call.
import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, AlertDescription, AlertIcon, Badge, Box, Button, Card, CardBody, CardHeader, Divider, Flex, Heading, HStack,
  Icon, Link, Progress, SimpleGrid, Skeleton, Stat, StatHelpText, StatLabel, StatNumber, Table, Tbody, Td, Text, Th,
  Thead, Tooltip, Tr, VStack, Wrap, WrapItem, useColorModeValue,
} from '@chakra-ui/react';
import {
  MdAutoMode, MdCheckCircle, MdCloudOff, MdHub, MdInsights, MdModelTraining, MdOpenInNew, MdPsychology, MdStorage, MdSync,
  MdTimeline, MdWarning,
} from 'react-icons/md';
import { ExplainButton } from './FlowSenseAIExplainer';
import {
  DECISION_COLOR, DECISION_LABEL, ENGINE_COLOR, EVENT_COLOR, EVENT_LABEL, JOB_COLOR, LEVEL_COLOR, fmtBytes, fmtMetric, fmtNumber, timeAgo,
} from './mlFormat';

const MODULE_LINKS = [
  { key: 'dashboard', name: 'Dashboard', path: '/admin/dashboard', ai: ['RUL_PREDICTION', 'ASSISTANT'] },
  { key: 'workorders', name: 'Work Orders', path: '/admin/workorders', ai: ['DISPATCHER', 'PART_PREDICTION', 'ISSUE_CLASSIFIER', 'SLA_GRADING'] },
  { key: 'sla', name: 'SLA Command Center', path: '/admin/sla-ai', ai: ['SLA_GRADING', 'DISPATCHER'] },
  { key: 'helpdesk', name: 'Helpdesk & Issues', path: '/admin/helpdesk', ai: ['ISSUE_TRIAGE', 'ISSUE_CLASSIFIER'] },
  { key: 'readings', name: 'Live Readings', path: '/admin/readings', ai: ['ANOMALY_DETECTION', 'FLOOD_RISK'] },
  { key: 'utilities', name: 'Public Utilities', path: '/admin/utilities', ai: ['RUL_PREDICTION', 'PRESCRIPTIVE'] },
  { key: 'roads', name: 'Road Works', path: '/admin/road-works', ai: ['POTHOLE_VISION', 'ROAD_CONDITION'] },
  { key: 'inventory', name: 'Inventory', path: '/admin/inventory', ai: ['INVENTORY_DEMAND', 'PART_PREDICTION'] },
  { key: 'analytics', name: 'Analytics Hub', path: '/admin/analytics', ai: ['RUL_PREDICTION', 'DISPATCHER', 'PART_PREDICTION', 'ISSUE_CLASSIFIER', 'PRESCRIPTIVE', 'ANOMALY_DETECTION'] },
  { key: 'twin', name: 'FlowSense Hub', path: '/admin/digital-twin', ai: ['RUL_PREDICTION'] },
];

const TABLE_LABEL = {
  work_orders: 'Work orders', issues: 'Issues', assets: 'Assets', public_utilities: 'Utility assets', sensors: 'Sensors',
  sensor_readings: 'Sensor readings', water_level_readings: 'Well level readings', pressure_readings: 'Pressure readings',
  dispatch_feedback_log: 'Dispatch feedback (AI Dispatcher)', work_order_inventory: 'Parts used (Parts Predictor)',
  work_order_feedback: 'Technician feedback (NLP)', work_order_suggested_parts: 'AI part suggestions', part_suggestion_feedback: 'Part suggestion grades',
  historical_failures: 'Asset failures (RUL)', asset_rul_predictions: 'RUL predictions', detected_anomalies: 'Detected anomalies',
  anomaly_telemetry: 'Anomaly telemetry', active_sensor_alerts: 'Active sensor alerts', road_segments: 'Road segments',
  road_inspections: 'Road inspections', road_rul_predictions: 'Road RUL predictions', inventory_items: 'Inventory items',
  prescriptive_actions_applied: 'Prescriptive actions applied', users: 'Users',
};

function SummaryStat({ label, value, help, icon, color = 'purple', loading }) {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const iconBg = useColorModeValue(`${color}.50`, 'whiteAlpha.100');
  return (
    <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
      <Flex justify="space-between" align="flex-start">
        <Stat>
          <StatLabel fontSize="xs" textTransform="uppercase" letterSpacing="wide">{label}</StatLabel>
          {loading ? <Skeleton h="28px" w="60px" mt={1} /> : <StatNumber fontSize="2xl">{value}</StatNumber>}
          {help && <StatHelpText mb={0} fontSize="xs">{help}</StatHelpText>}
        </Stat>
        <Box bg={iconBg} p={2} borderRadius="lg"><Icon as={icon} boxSize={5} color={`${color}.400`} /></Box>
      </Flex>
    </Box>
  );
}

function ModelCard({ m, canManage, onTrain, onOpen, training }) {
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const sub = useColorModeValue('gray.50', 'whiteAlpha.50');
  const active = m.activeJob;
  const need = m.policy?.minNewRecords || 0;
  const fresh = m.data?.newSinceTraining;
  const pct = need && fresh !== null && fresh !== undefined ? Math.min(100, Math.round((fresh / need) * 100)) : null;
  return (
    <Card bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" variant="outline">
      <CardHeader pb={2}>
        <Flex justify="space-between" align="flex-start" gap={2}>
          <Box>
            <HStack spacing={2}>
              <Heading size="sm">{m.name}</Heading>
              {m.champion ? <Badge colorScheme="green">in production</Badge> : <Badge colorScheme="orange">not trained</Badge>}
            </HStack>
            <Text fontSize="xs" color={muted} mt={1}>{m.short}</Text>
          </Box>
          <ExplainButton topic={m.key} />
        </Flex>
      </CardHeader>
      <CardBody pt={0}>
        <SimpleGrid columns={2} spacing={3} bg={sub} p={3} borderRadius="lg">
          <Box>
            <Text fontSize="xs" color={muted}>Production · {m.metric.label}</Text>
            <Text fontSize="xl" fontWeight="700">{m.champion ? fmtMetric(m.champion.metric, m.metric) : '—'}</Text>
            <Text fontSize="xs" color={muted} noOfLines={1}>{m.champion ? `v${m.champion.model_version} · ${timeAgo(m.champion.activated_at)}` : 'Fallback logic in use'}</Text>
          </Box>
          <Box>
            <Text fontSize="xs" color={muted}>Training data</Text>
            <Text fontSize="xl" fontWeight="700">{m.data?.records !== undefined ? fmtNumber(m.data.records) : '—'}</Text>
            <Text fontSize="xs" color={muted} noOfLines={1}>{m.data?.error ? 'source unavailable' : fresh !== null && fresh !== undefined ? `${fmtNumber(fresh)} new since training` : `min ${m.data?.minRecords}`}</Text>
          </Box>
        </SimpleGrid>

        {m.challenger && (
          <HStack mt={3} spacing={2} fontSize="xs">
            <Badge colorScheme="purple">challenger</Badge>
            <Text noOfLines={2}>v{m.challenger.model_version} · {fmtMetric(m.challenger.metric, m.metric)} — {m.challenger.verdict?.decision === 'promote' ? 'beats production' : 'below production'}</Text>
          </HStack>
        )}

        {active ? (
          <Box mt={3}>
            <Flex justify="space-between" fontSize="xs" mb={1}><Text>{active.stage || 'Training'}</Text><Text>{active.progress}%</Text></Flex>
            <Progress value={active.progress} size="sm" colorScheme="blue" borderRadius="full" hasStripe isAnimated />
          </Box>
        ) : (
          <Box mt={3}>
            <Flex justify="space-between" fontSize="xs" mb={1} color={muted}>
              <HStack spacing={1}><Icon as={MdAutoMode} /><Text>Autopilot {m.policy?.enabled ? 'on' : 'off'}</Text></HStack>
              {pct !== null && <Text>{pct}% to next retrain</Text>}
            </Flex>
            {pct !== null && <Progress value={pct} size="xs" colorScheme={m.autopilot?.ready ? 'green' : 'purple'} borderRadius="full" />}
            <Text fontSize="xs" color={muted} mt={1} noOfLines={2}>{m.autopilot?.reason}</Text>
          </Box>
        )}

        <Flex mt={3} gap={2} wrap="wrap" align="center">
          {m.lastJob && (
            <Tooltip label={m.lastJob.error || m.lastJob.stage || ''} hasArrow>
              <Badge colorScheme={m.lastJob.decision ? DECISION_COLOR[m.lastJob.decision] : JOB_COLOR[m.lastJob.status]} variant="subtle">
                last run: {m.lastJob.decision ? DECISION_LABEL[m.lastJob.decision] : m.lastJob.status} · {timeAgo(m.lastJob.finished_at || m.lastJob.created_at)}
              </Badge>
            </Tooltip>
          )}
          {m.engine?.inSync === false && <Badge colorScheme="red">engine out of sync</Badge>}
          {m.engine?.baselineStored && <Tooltip label="The ML engine stores a training baseline for drift checks" hasArrow><Badge colorScheme="cyan" variant="subtle">drift baseline</Badge></Tooltip>}
        </Flex>
        <Divider my={3} />
        <Flex gap={2} wrap="wrap">
          {canManage && (
            <Button size="sm" colorScheme="purple" leftIcon={<Icon as={MdModelTraining} />} onClick={() => onTrain(m.key)} isLoading={training} isDisabled={Boolean(active)}>
              {active ? 'Training…' : 'Train now'}
            </Button>
          )}
          <Button size="sm" variant="outline" leftIcon={<Icon as={MdTimeline} />} onClick={() => onOpen(m.key)}>Versions & metrics</Button>
        </Flex>
        <Wrap mt={3} spacing={1}>
          {m.usedBy.map(u => (
            <WrapItem key={u.label}><Link as={RouterLink} to={u.path} fontSize="xs" color="purple.400">{u.label}</Link><Text fontSize="xs" color={muted} mx={1}>·</Text></WrapItem>
          ))}
        </Wrap>
      </CardBody>
    </Card>
  );
}

export default function IntelligenceOverview({ overview: ov, loading, error, onRetry, canManage, onTrain, onOpenModel, trainingKey, onOpenEngine }) { // --- UPDATE (engines): + onOpenEngine
  const bg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const chipBg = useColorModeValue('purple.50', 'whiteAlpha.100');

  const nameOf = useMemo(() => {
    const map = {};
    (ov?.models || []).forEach(m => { map[m.key] = m.name; });
    (ov?.engines || []).forEach(e => { map[e.key] = e.name; });
    return map;
  }, [ov]);
  const stateOf = useMemo(() => {
    const map = {};
    (ov?.models || []).forEach(m => { map[m.key] = m.champion ? 'online' : 'idle'; });
    (ov?.engines || []).forEach(e => { map[e.key] = e.state; });
    return map;
  }, [ov]);
  const tables = useMemo(() => Object.entries(ov?.data || {}).sort((a, b) => b[1].rows - a[1].rows), [ov]);

  if (error && !ov) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        <AlertDescription flex="1">{error.status === 403 ? 'Your role cannot view ML operations. Ask FlowSense AI below still works for you.' : `Could not load the AI overview: ${error.message}`}</AlertDescription>
        {error.status !== 403 && <Button size="sm" onClick={onRetry}>Retry</Button>}
      </Alert>
    );
  }

  const s = ov?.summary || {};
  return (
    <VStack align="stretch" spacing={5}>
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3}>
        <SummaryStat label="Models in production" value={`${s.inProduction ?? 0}/${s.trainable ?? 4}`} icon={MdPsychology} loading={loading && !ov} />
        <SummaryStat label="Versions in registry" value={fmtNumber(s.totalVersions)} icon={MdStorage} color="blue" loading={loading && !ov} />
        <SummaryStat label="Training runs active" value={s.activeJobs ?? 0} icon={MdSync} color="cyan" loading={loading && !ov} />
        <SummaryStat label="ML engine" value={s.mlOnline ? 'Online' : 'Offline'} icon={s.mlOnline ? MdCheckCircle : MdCloudOff} color={s.mlOnline ? 'green' : 'red'} help={ov?.mlEngine?.error || undefined} loading={loading && !ov} />
        <SummaryStat label="Autopilot" value={s.autopilotEnabled ? 'On' : 'Off'} icon={MdAutoMode} color={s.autopilotEnabled ? 'green' : 'gray'} help={ov?.autopilot?.lastSweep ? `checked ${timeAgo(ov.autopilot.lastSweep.at)}` : undefined} loading={loading && !ov} />
        <SummaryStat label="Assistant LLM" value={s.assistantOnline ? 'Online' : 'Built-in'} icon={MdInsights} color={s.assistantOnline ? 'purple' : 'gray'} help={ov?.assistant?.model} loading={loading && !ov} />
      </SimpleGrid>

      {ov?.attention?.length > 0 && (
        <VStack align="stretch" spacing={2}>
          {ov.attention.map((a, i) => (
            <Alert key={i} status={a.level === 'high' ? 'error' : a.level === 'medium' ? 'warning' : 'info'} borderRadius="lg" py={2}>
              <AlertIcon as={MdWarning} color={`${LEVEL_COLOR[a.level]}.400`} />
              <AlertDescription fontSize="sm" flex="1">{a.text}</AlertDescription>
              {a.modelType && <Button size="xs" variant="ghost" onClick={() => onOpenModel(a.modelType)}>Open</Button>}
            </Alert>
          ))}
        </VStack>
      )}

      <Box>
        <Heading size="sm" mb={3}>Trainable models</Heading>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {loading && !ov
            ? [0, 1, 2, 3].map(i => <Skeleton key={i} h="300px" borderRadius="xl" />)
            : (ov?.models || []).map(m => <ModelCard key={m.key} m={m} canManage={canManage} onTrain={onTrain} onOpen={onOpenModel} training={trainingKey === m.key} />)}
        </SimpleGrid>
      </Box>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <HStack mb={3}><Icon as={MdHub} color="purple.400" /><Heading size="sm">System map — where AI runs in FlowSense</Heading></HStack>
          <VStack align="stretch" spacing={2}>
            {MODULE_LINKS.map(mod => (
              <Flex key={mod.key} align="center" gap={3} wrap="wrap">
                <Link as={RouterLink} to={mod.path} fontWeight="600" fontSize="sm" minW="150px">{mod.name} <Icon as={MdOpenInNew} boxSize={3} /></Link>
                <Wrap spacing={1} flex="1">
                  {mod.ai.map(k => (
                    <WrapItem key={k}>
                      <Badge bg={chipBg} borderRadius="full" px={2} textTransform="none" fontWeight="500">
                        <Box as="span" display="inline-block" w="6px" h="6px" borderRadius="full" bg={`${ENGINE_COLOR[stateOf[k]] || 'gray'}.400`} mr={1} />
                        {nameOf[k] || k}
                      </Badge>
                    </WrapItem>
                  ))}
                </Wrap>
              </Flex>
            ))}
          </VStack>
        </Box>

        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <HStack mb={3} justify="space-between">
            <HStack><Icon as={MdInsights} color="purple.400" /><Heading size="sm">AI engines</Heading></HStack>
            <ExplainButton topic="overview" label="Explain FlowSense" />
          </HStack>
          <VStack align="stretch" spacing={2}>
            {(ov?.engines || []).map(e => (
              <Flex key={e.key} align="center" gap={3}>
                <Badge colorScheme={ENGINE_COLOR[e.state] || 'gray'} minW="78px" textAlign="center">{e.state.replace('_', ' ')}</Badge>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" fontWeight="600">{e.name}</Text>
                  <Text fontSize="xs" color={muted} noOfLines={1}>{e.detail || e.short}</Text>
                  {/* --- UPDATE (engines): live numbers */}
                  {e.insight?.headline && <Text fontSize="xs" color="purple.400" fontWeight="600" noOfLines={1}>{e.insight.headline}</Text>}
                  {/* --- END UPDATE */}
                </Box>
                {onOpenEngine && <Button size="xs" variant="ghost" onClick={() => onOpenEngine(e.key)}>{e.hasInsights ? 'Details' : 'Open'}</Button>} {/* --- UPDATE (engines) */}
                <ExplainButton topic={e.key} label="" aria-label={`Explain ${e.name}`} />
              </Flex>
            ))}
            {!ov && loading && [0, 1, 2, 3].map(i => <Skeleton key={i} h="34px" />)}
          </VStack>
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} overflowX="auto">
          <HStack mb={3}><Icon as={MdStorage} color="purple.400" /><Heading size="sm">Data behind the models</Heading></HStack>
          <Table size="sm">
            <Thead><Tr><Th>Table</Th><Th isNumeric>Rows (est.)</Th><Th isNumeric>Size</Th></Tr></Thead>
            <Tbody>
              {tables.map(([t, v]) => (
                <Tr key={t}><Td><Text fontSize="sm">{TABLE_LABEL[t] || t}</Text><Text fontSize="xs" color={muted}>{t}</Text></Td><Td isNumeric>{fmtNumber(v.rows)}</Td><Td isNumeric>{fmtBytes(v.bytes)}</Td></Tr>
              ))}
              {!tables.length && <Tr><Td colSpan={3}><Text fontSize="sm" color={muted}>{loading ? 'Loading…' : 'No table statistics available.'}</Text></Td></Tr>}
            </Tbody>
          </Table>
        </Box>
        <Box bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4}>
          <HStack mb={3}><Icon as={MdTimeline} color="purple.400" /><Heading size="sm">Latest model activity</Heading></HStack>
          <VStack align="stretch" spacing={2} maxH="420px" overflowY="auto">
            {(ov?.recentEvents || []).map(e => (
              <Flex key={e.id} gap={3} align="flex-start">
                <Badge colorScheme={EVENT_COLOR[e.event] || 'gray'} minW="96px" textAlign="center">{EVENT_LABEL[e.event] || e.event}</Badge>
                <Box flex="1" minW={0}>
                  <Text fontSize="sm" noOfLines={1}>{nameOf[e.model_type] || e.model_type}{e.model_version ? ` · v${e.model_version}` : ''}</Text>
                  <Text fontSize="xs" color={muted} noOfLines={2}>{e.actor_label || 'system'} · {timeAgo(e.created_at)}{e.details?.verdict?.reason ? ` — ${e.details.verdict.reason}` : e.details?.error ? ` — ${e.details.error}` : ''}</Text>
                </Box>
              </Flex>
            ))}
            {ov && !ov.recentEvents?.length && <Text fontSize="sm" color={muted}>No model activity yet. Train a model to start the history.</Text>}
          </VStack>
        </Box>
      </SimpleGrid>
    </VStack>
  );
}
